import { ApiResponse, BaseApiResponse } from '@core/swagger-client';
import { ExamResult, ExamResultType, QuestionAnswer } from '@core/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { LiveWebinarConnection } from '../signal-r/live-webinar-connection';
import { OnDemandWebinarConnection } from '../signal-r/on-demand-webinar-connection';
import { setLastServerAction } from '../slices/live-webinar-slice';
import { onDemandSetLastServerAction } from '../slices/on-demand-webinar-slice';
import { nextQuestion, previousQuestion, questionnaireResultUpdated, refreshQuestionnaireResult, retryQuestionnaire } from '../slices/questionnaire-slice';
import { RootState } from '../store';

function* handleSubmitAnswer(action: PayloadAction<QuestionAnswer | null>) {
    const isFinalized: boolean = yield select((state: RootState) => state.questionnaire.answers?.isFinalized || false);
    if (isFinalized) {
        return;
    }

    if (!action.payload) {
        return;
    }

    const type: 'survey' | 'exam' | null = yield select((state: RootState) => state.questionnaire.type);
    if (!type) {
        return;
    }

    const liveConnection: LiveWebinarConnection = yield getContext('liveConnection');
    if (liveConnection.isConnected) {
        try {
            const submitAnswer = type === 'survey' ? liveConnection.submitSurveyAnswer : liveConnection.submitExamAnswer;
            const result: BaseApiResponse = yield call(
                [liveConnection, submitAnswer],
                action.payload.questionId,
                action.payload.openAnswer ?? null,
                action.payload.selectedAnswers ?? null
            );
            yield put(setLastServerAction({ method: 'submitAnswer', result }));
        } catch (e) {
            yield put(
                onDemandSetLastServerAction({
                    method: 'submitAnswer',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }

    const onDemandConnection: OnDemandWebinarConnection = yield getContext('onDemandConnection');
    if (onDemandConnection.isConnected) {
        try {
            const submitAnswer = type === 'survey' ? onDemandConnection.submitSurveyAnswer : onDemandConnection.submitExamAnswer;
            const result: BaseApiResponse = yield call(
                [onDemandConnection, submitAnswer],
                action.payload.questionId,
                action.payload.openAnswer ?? null,
                action.payload.selectedAnswers ?? null
            );
            yield put(onDemandSetLastServerAction({ method: 'submitAnswer', result }));
        } catch (e) {
            yield put(
                onDemandSetLastServerAction({
                    method: 'submitAnswer',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }
}

function* handleRefreshQuestionnaireResult(action: PayloadAction) {
    const type: 'survey' | 'exam' | null = yield select((state: RootState) => state.questionnaire.type);
    if (type !== 'exam') {
        yield put(
            questionnaireResultUpdated({
                result: ExamResultType.NotAvailable
            })
        );
        return;
    }

    // Delay for 1.5 seconds to allow the server to process the exam result
    yield delay(1500);

    const liveConnection: LiveWebinarConnection = yield getContext('liveConnection');
    if (liveConnection.isConnected) {
        try {
            const result: ApiResponse<ExamResult> = yield liveConnection.getExamResult();
            if (result.success && result.data) {
                yield put(questionnaireResultUpdated(result.data));
            }
            yield put(setLastServerAction({ method: 'getExamResult', result }));
        } catch (e) {
            yield put(
                setLastServerAction({
                    method: 'getExamResult',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }

    const onDemandConnection: OnDemandWebinarConnection = yield getContext('onDemandConnection');
    if (onDemandConnection.isConnected) {
        try {
            const result: ApiResponse<ExamResult> = yield onDemandConnection.getExamResult();
            if (result.success && result.data) {
                yield put(questionnaireResultUpdated(result.data));
            }
            yield put(onDemandSetLastServerAction({ method: 'getExamResult', result }));
        } catch (e) {
            yield put(
                onDemandSetLastServerAction({
                    method: 'getExamResult',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }
}

function* handleRetryQuestionnaire(action: PayloadAction) {
    const type: 'survey' | 'exam' | null = yield select((state: RootState) => state.questionnaire.type);
    if (type !== 'exam') {
        return;
    }

    const liveConnection: LiveWebinarConnection = yield getContext('liveConnection');
    if (liveConnection.isConnected) {
        try {
            const result: BaseApiResponse = yield liveConnection.retryExam();
            yield put(onDemandSetLastServerAction({ method: 'retryExam', result }));
        } catch (e) {
            yield put(
                onDemandSetLastServerAction({
                    method: 'retryExam',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }

    const onDemandConnection: OnDemandWebinarConnection = yield getContext('onDemandConnection');
    if (onDemandConnection.isConnected) {
        try {
            const result: BaseApiResponse = yield onDemandConnection.retryExam();
            yield put(onDemandSetLastServerAction({ method: 'retryExam', result }));
        } catch (e) {
            yield put(
                onDemandSetLastServerAction({
                    method: 'retryExam',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
    }
}

export function* watchQuestionnaire() {
    yield takeEvery(previousQuestion, handleSubmitAnswer);
    yield takeEvery(nextQuestion, handleSubmitAnswer);
    yield takeEvery(refreshQuestionnaireResult, handleRefreshQuestionnaireResult);
    yield takeEvery(retryQuestionnaire, handleRetryQuestionnaire);
}
